import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Main governance hook for fetching and managing proposals
 * Now with Page Visibility API, AbortController support, response caching, and search/filter support
 */
export function useGovernance(category = '', additionalParams = {}) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const abortControllerRef = useRef(null);
  const cacheRef = useRef({}); // Cache: { key: { data, timestamp } }

  // Cache TTL: 10 seconds
  const CACHE_TTL = 10000;

  // Fetch proposals from WordPress backend
  const fetchProposals = useCallback(async (force = false) => {
    // Build cache key including search params
    const paramStr = JSON.stringify(additionalParams);
    const cacheKey = `${category || 'all'}_${paramStr}`;
    const now = Date.now();

    // Check cache (skip if force refresh)
    if (!force && cacheRef.current[cacheKey]) {
      const cached = cacheRef.current[cacheKey];
      const age = now - cached.timestamp;

      if (age < CACHE_TTL) {
        console.log(`Using cached data for ${cacheKey} (age: ${Math.round(age/1000)}s)`);
        setProposals(cached.data);
        setLastFetch(cached.timestamp);
        setLoading(false);
        return;
      }
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (category) params.append('category', category);

      // Add additional search/filter parameters
      Object.keys(additionalParams).forEach(key => {
        if (additionalParams[key]) {
          params.append(key, additionalParams[key]);
        }
      });

      const response = await fetch(
        `${window.t24Governance.ajaxUrl}?action=t24_get_governance_proposals&${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch proposals');
      }

      const data = await response.json();

      if (data.success && data.data && data.data.proposals) {
        const proposalsData = data.data.proposals;
        setProposals(proposalsData);
        const timestamp = Date.now();
        setLastFetch(timestamp);

        // Cache the response
        cacheRef.current[cacheKey] = {
          data: proposalsData,
          timestamp: timestamp
        };

        console.log(`Fetched and cached ${proposalsData.length} proposals for ${cacheKey}`);
      } else {
        throw new Error(data.data?.message || 'Failed to load proposals');
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        console.log('Fetch aborted');
        return;
      }
      console.error('Error fetching proposals:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [category, additionalParams]);

  // Initial fetch
  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Auto-refresh every 30 seconds with Page Visibility API (force refresh to bypass cache)
  useEffect(() => {
    let interval = null;

    const startInterval = () => {
      // Only start if not already running
      if (!interval) {
        interval = setInterval(() => {
          fetchProposals(true); // Force refresh on interval to get fresh data
        }, 30000);
      }
    };

    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - stop polling to save battery
        stopInterval();
      } else {
        // Tab visible - resume polling and fetch immediately (force refresh)
        fetchProposals(true);
        startInterval();
      }
    };

    // Start interval immediately
    startInterval();

    // Listen to visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Abort any pending requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchProposals]);

  // Refetch manually
  const refetch = useCallback(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Group proposals by status (dynamically based on actual proposal statuses)
  const proposalsByStatus = useCallback(() => {
    const grouped = {};

    proposals.forEach((proposal) => {
      const status = proposal.status || 'pending';
      if (!grouped[status]) {
        grouped[status] = [];
      }
      grouped[status].push(proposal);
    });

    return grouped;
  }, [proposals]);

  return {
    proposals,
    proposalsByStatus: proposalsByStatus(),
    loading,
    error,
    refetch,
    lastFetch,
  };
}
